/* LeftSidebar — Raynet-style vertical navigation (v4.9).
 *
 * Always visible on left edge (above all overlays incl. ClientFolderHost).
 * Icons + collapsible labels. Active section highlighted with cognac accent.
 *
 * Width: 64px collapsed (icons only), 200px expanded (icons + labels).
 * Hover expansion enabled for desktop; locked-expanded persisted in localStorage.
 *
 * ════════════════════════════════════════════════════════════════════════
 * BARVY (přepsáno 17. 8. 2026 — „druhá vrstva" opravy přepínání motivu)
 * ════════════════════════════════════════════════════════════════════════
 * Lišta je vidět na KAŽDÉ obrazovce, ale měla barvy natvrdo (19 tokenů proti
 * 28 hexům), takže po přepnutí na Indigo zůstala jediná zelená… pardon,
 * inkoustová. Půl appky přebarvené je horší než nepřebarvená.
 * Teď jedou barvy přes rodinu --sidebar* / --accent* (viz index.css).
 *
 * CO TU SMÍ ZŮSTAT NATVRDO A PROČ:
 *  1) LOGO beplan (#7FA9E8 „be" + #F47920 „plan" a šipka). Značka se
 *     s motivem aplikace nepřebarvuje — to platí i pro tisk a vizitky.
 *  2) BAREVNÁ LEGENDA TYPŮ ZÁZNAMU v menu „Nový záznam" (lead = zelená
 *     #0AA653, obchod = fialová #6D48B8, nábor = zlatá #D69412, úkol =
 *     cihlová #C74E22, doporučení = purpurová #A4436B). Není to chrome,
 *     je to sémantika sdílená s 13–36 dalšími soubory (lib/dealMeta.js,
 *     lib/naborFaze.js, StageTrack, pipeline, radar…). Kdyby se přebarvila
 *     jen tady, rozejde se legenda s celou zbylou appkou — stejné pravidlo
 *     jako u --money (peníze jsou zelené v obou motivech).
 *     „Nový klient" je výjimka z výjimky: jde o primární akci, jede na
 *     var(--accent) a v Sage vychází přesně původní #8C3B2E.
 *  3) rgba(255,255,255,x) — bílá s alfou (text, linky, výplně). Lišta je
 *     TMAVÁ V OBOU motivech (--sidebar #26221A v Sage, #161B2E v Indigu),
 *     takže bílý text na ní je legitimní v obou. Kdyby někdy vznikl motiv
 *     se světlou lištou, musí se tohle přepsat spolu s ním.
 * ════════════════════════════════════════════════════════════════════════
 */
import React, { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useMaTym } from "@/lib/mujTym";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProfile } from "@/contexts/ProfileContext";
import { odebirejZemi, zemeTenanta } from "@/lib/tenantMena";
import { zapnuto } from "@/lib/funkce";
import OdkazNovinky from "@/components/novinky/OdkazNovinky";

/* NAVIGACE VE VŠECH PĚTI JAZYCÍCH
 * ------------------------------------------------------------------
 * Do 17. 8. 2026 tu byl overlay `SK_NAV` aplikovaný jen když `lang === "sk"`.
 * Uměl přesně dva jazyky, takže vietnamec i ukrajinec dostali ČESKOU navigaci
 * na KAŽDÉ obrazovce — nejviditelnější prvek appky, a přitom mimo statistiku
 * dvoujazyčných ternárů (není to ternár, je to mapa).
 *
 * Teď: český text je klíč do slovníku `lista.*` (data/translations/*.js),
 * který má všech pět jazyků. Slovenské hodnoty jsou 1:1 převzaté z původního
 * SK_NAV, takže se slovenština nemohla pohnout.
 *
 * Když klíč chybí, `t()` vrátí fallback = původní český text → chování jako
 * dřív, nikdy prázdno.
 */
const NAV_KLIC = {
  // hlavní sekce
  "Nástěnka": "nastenka", "Klienti": "klienti", "Obchody": "obchody", "Diář": "diar",
  "Pošta": "posta", "Manažer": "manazer", "Vzdělávání": "vzdelavani", "Nástroje": "nastroje",
  // Klienti — podpoložky
  "Adresář": "adresar", "Zrcadlo volání": "zrcadloVolani", "Pipeline klientů": "pipelineKlientu",
  "Radar příležitostí": "radarPrilezitosti", "Mapa klientů": "mapaKlientu",
  "Leady (Projekt 100)": "leadyProjekt100",
  // Obchody — podpoložky
  "Obchodní nástěnka": "obchodniNastenka", "Hypoteční pipeline": "hypotecniPipeline",
  "Náborová pipeline": "naborovaPipeline", "Náborový radar": "naborovyRadar",
  "Realitní pipeline": "realitniPipeline", "Zakázky (reality)": "zakazkyReality", "Rezervační smlouva": "rezervacniSmlouva",
  // Manažer — podpoložky
  "Přehled týmu": "prehledTymu", "IK konzultace": "ikKonzultace", "Moji poradci": "mojiPoradci",
  "Vyhlášení výsledků": "vyhlaseniVysledku", "Týmové akce": "tymoveAkce", "Diář týmu": "diarTymu",
  // Vzdělávání — podpoložky
  "7denní adaptace": "adaptace7", "Adaptace týmu": "adaptaceTymu",
  "Start nováčka (Cesta 90 dní)": "startNovacka",
  "Akademie — moduly & infografiky": "akademieModuly",
  "Metodika: Sestavení plánu": "metodikaPlan",
  "Zkouška & certifikát": "zkouskaCertifikat",
  "Skripty & námitky": "skriptyNamitky",
  "Kde shánět klienty": "kdeShanetKlienty",
  "Videa & kurzy": "videaKurzy",
  // Nástroje — podpoložky
  "Rozcestník nástrojů": "rozcestnikNastroju",
  "Finanční matematika": "financniMatematika",
  "Kalkulačky (20)": "kalkulacky21",
  "Penzijní kalkulačka": "penzijniKalkulacka",
  "Životní pojištění": "zivotniPojisteni",
  "Historická kalkulačka": "historickaKalkulacka",
  "Investiční strategie": "investicniStrategie",
  "Srovnávač fondů": "srovnavacFondu",
  "Nemovitost na pronájem": "nemovitostPronajem",
  "Provizní kalkulačka": "provizniKalkulacka",
  // chrome lišty
  "Hledat": "hledat", "Sbalit": "sbalit", "Připnout": "pripnout",
};

/* SLOVENSKÉ NÁZVY — historický overlay `SK_NAV`, dnes UŽ NE překladová cesta.
 * Opsáno znak po znaku z verze před přechodem na t() (git 4470f12). Zůstává tu
 * jako DOKLAD slovenštiny pro `__tests__/listaZalozkyPetJazyku.test.js`: ten
 * porovná tuhle tabulku se slovníkem sk.js a spadne, kdyby se slovenské znění
 * pohnulo. Majitel s ním jezdí na SK, takže se měnit nesmí.
 * Čtyři páry z původní mapy („Kontaktní osoby", „Hromadný email", „Nastavení",
 * „Odhlásit") tu schválně nejsou — ty položky lišta nerenderovala už před
 * zásahem (Nastavení a Odhlásit se přesunuly do profilu). */
const SK_NAV = {
  "Nástěnka": "Nástenka", "Adresář": "Adresár",
  "Obchodní nástěnka": "Obchodná nástenka", "Hypoteční pipeline": "Hypotekárna pipeline",
  "Diář": "Diár", "Manažer": "Manažér", "Vzdělávání": "Vzdelávanie",
  "Sbalit": "Zbaliť", "Připnout": "Pripnúť", "Hledat": "Hľadať",
  "Radar příležitostí": "Radar príležitostí", "Mapa klientů": "Mapa klientov",
  "7denní adaptace": "7-dňová adaptácia", "Adaptace týmu": "Adaptácia tímu",
  "Start nováčka (Cesta 90 dní)": "Štart nováčika (Cesta 90 dní)",
  "Akademie — moduly & infografiky": "Akadémia — moduly a infografiky",
  "Metodika: Sestavení plánu": "Metodika: Zostavenie plánu",
  "Zkouška & certifikát": "Skúška a certifikát",
  "Skripty & námitky": "Skripty a námietky",
  "Kde shánět klienty": "Kde zháňať klientov",
  "Videa & kurzy": "Videá a kurzy",
  "Rozcestník nástrojů": "Rozcestník nástrojov",
  "Finanční matematika": "Finančná matematika",
  "Penzijní kalkulačka": "Dôchodková kalkulačka",
  "Životní pojištění": "Životné poistenie",
  "Investiční strategie": "Investičné stratégie",
  "Srovnávač fondů": "Porovnávač fondov",
  "Nemovitost na pronájem": "Nehnuteľnosť na prenájom",
  "Provizní kalkulačka": "Provízna kalkulačka",
};

/** Český text → překlad pro aktuální jazyk. Fallback = původní text. */
const navT = (s, t) => (NAV_KLIC[s] ? t(`lista.${NAV_KLIC[s]}`, s) : s);

// Export jen pro testy (__tests__/listaZalozkyPetJazyku.test.js) — hlídají, že
// každá položka lišty má klíč ve všech pěti slovnících a že slovenština sedí.
export { NAV_KLIC, SK_NAV };
import { createPortal } from "react-dom";
// QuickAddContactModalV2 odpojen 4. 7. 2026 — „Nový klient" z FAB teď otevírá plný
// AddClientModal přes /klienti?new=1 (sjednoceno s pravým tlačítkem „Manuálně").
import {
  Home, Users, Calendar, GraduationCap, Wrench, BarChart3,
  Shield, Brain, Mic, ChevronsLeft, ChevronsRight, UserCheck, Mail,
  Briefcase, ChevronRight, Plus, UserPlus, Target, ListTodo, Gift, X, ShieldAlert, Megaphone, Search,
} from "lucide-react";

const W_COLLAPSED = 64;
const W_EXPANDED = 172;  // užší — v liště bylo moc mrtvého prostoru (feedback 7. 7.)
// Mobil = pod tímto breakpointem (< lg). Na mobilu je sidebar off-canvas drawer.
const MOBILE_BREAKPOINT = 1024;

// Sledování šířky okna — rozlišení desktop vs. mobil (drawer).
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    onChange();
    // matchMedia change + resize fallback (resize spolehlivější napříč prostředími)
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange); // Safari < 14
    window.addEventListener("resize", onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
      window.removeEventListener("resize", onChange);
    };
  }, []);
  return isMobile;
}

export default function LeftSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Skupina Manažer: role NEBO potvrzený tým (14. 10. 2026, lib/mujTym).
  const maTymUzivatel = useMaTym(user);
  const { t } = useLanguage();
  /* Penzijko (DPS/DIP) je jen pro ČR — mimo ČR odkaz nevede nikam užitečného (24. 10. 2026). */
  const zeme = useSyncExternalStore(odebirejZemi, zemeTenanta, zemeTenanta);
  const isMobile = useIsMobile();
  const [locked, setLocked] = useState(() => localStorage.getItem("sidebar-locked") === "1");
  // Žádné hover-rozbalování lišty (uživatel: „buď se zavře nebo sbalí, hotovo").
  // Šířku řídí JEN připnutí (toggle dole). Submenu v sbalené liště = flyout na hover.
  // Mobil: off-canvas drawer. Default skrytý; otevře hamburger (event sidebar-open).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const expanded = isMobile ? true : locked;

  // Hlavička: fotka poradce (z profilu, nastaví v Nastavení) + zkratka hledání dle platformy.
  const { profile } = useProfile();
  const advisorPhoto = profile?.photo_base64 || "";
  const advisorInitials = ((user?.name || "P").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("") || "P").toUpperCase();
  const kbdHint = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? "⌘K" : "Ctrl K";

  // „Nový záznam" — statické tlačítko (dříve plovoucí FAB jen na /crm, mizel). Menu typů + Quick Add.
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const newBtnRef = useRef(null);
  const openNewMenu = () => {
    const r = newBtnRef.current?.getBoundingClientRect();
    if (r) setMenuPos({ left: r.right + 8, bottom: window.innerHeight - r.bottom });
    setNewMenuOpen((v) => !v);
  };
  useEffect(() => {
    if (!newMenuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setNewMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [newMenuOpen]);
  // Barvy níže = LEGENDA TYPŮ ZÁZNAMU, ne chrome motivu (viz hlavička souboru).
  // Zůstávají stejné v Sage i Indigu, aby se nerozešly se zbytkem appky.
  // Výjimka: „Nový klient" je primární akce → jede na akcentu motivu
  // (v Sage dá var(--accent) uvnitř .ink-board přesně původní #8C3B2E).
  const newActions = [
    { id: "new-client",   label: t("lista.novyKlient", "Nový klient"),   sub: t("lista.novyKlientSub", "Přidat do pipeline"),           icon: UserPlus,  accent: "var(--accent)",
      onClick: () => { setNewMenuOpen(false); navigate("/klienti?new=1"); } },
    /* „Nový lead“ pryč (velký test funkčnosti 24. 10. 2026): otevíral TENTÝŽ formulář
       jako „Nový klient“ (/klienti?new=1, nový kontakt je vždy ve fázi lead) — dvě
       položky, jedna akce. Klíče lista.novyLead* ve slovnících zůstávají. */
    { id: "new-deal",     label: t("lista.obchodniPripad", "Obchodní případ"),   sub: t("lista.obchodniPripadSub", "Rovnou založit obchod"),        icon: Briefcase, accent: "#6D48B8",
      onClick: () => { setNewMenuOpen(false); navigate("/obchody?newDeal=1"); } },
    { id: "new-nabor",    label: t("lista.naborKandidat", "Nábor — kandidát"),  sub: t("lista.naborKandidatSub", "Rovnou založit kandidáta"),     icon: Target,    accent: "#D69412",
      onClick: () => { setNewMenuOpen(false); navigate("/nabor?new=1"); } },
    { id: "new-task",     label: t("lista.ukolSchuzka", "Úkol / Schůzka"),   sub: t("lista.ukolSchuzkaSub", "Otevřít Diář"),                 icon: ListTodo,  accent: "#C74E22",
      onClick: () => { setNewMenuOpen(false); navigate("/diar?newActivity=1"); } },
    { id: "new-referral", label: t("lista.leadZDoporuceni", "Lead z doporučení"),   sub: t("lista.leadZDoporuceniSub", "Odkaz pro klienta"),            icon: Gift,      accent: "#A4436B",
      /* Odkaz pro klienta je v Leadovači (karta Doporučení), ne v Pipeline — dřív to
         vedlo na /crm/clients, kde žádný odkaz není (velký test funkčnosti 24. 10. 2026). */
      onClick: () => { setNewMenuOpen(false); navigate("/crm/leadgen"); } },
  ];

  useEffect(() => {
    localStorage.setItem("sidebar-locked", locked ? "1" : "0");
    // Notify AppLayout to adjust paddingLeft
    window.dispatchEvent(new CustomEvent("sidebar-toggle", { detail: { locked } }));
  }, [locked]);

  // ── Mobilní drawer: otevírání/zavírání přes globální eventy (hamburger) ──
  useEffect(() => {
    const open = () => setDrawerOpen(true);
    const close = () => setDrawerOpen(false);
    const toggle = () => setDrawerOpen((v) => !v);
    window.addEventListener("sidebar-open", open);
    window.addEventListener("sidebar-close", close);
    window.addEventListener("sidebar-drawer-toggle", toggle);
    return () => {
      window.removeEventListener("sidebar-open", open);
      window.removeEventListener("sidebar-close", close);
      window.removeEventListener("sidebar-drawer-toggle", toggle);
    };
  }, []);

  // Přepnutí na desktop → zavři drawer (aby nezůstal „viset" stav).
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // Escape zavře drawer; zablokuj scroll pozadí, když je drawer otevřený.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  // Zavři drawer při změně stránky (klik na položku navádí přes navigate).
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  const sections = [
    { id: "crm",          path: "/crm",          label: "Nástěnka",    icon: Home },
    { id: "klienti",      path: "/klienti",      label: "Klienti",     icon: Users,
      children: [
        { label: "Adresář",         path: "/klienti" },
        // UAT 8/2026: Zrcadlo volání a Pipeline byly JEN v podmenu uvnitř /crm,
        // takže se poradce k volání dostal vždycky přes Nástěnku. Denní práce
        // (obvolat → posunout fázi) musí být na jedno kliknutí.
        { label: "Zrcadlo volání",   path: "/crm/calls" },
        { label: "Pipeline klientů", path: "/crm/clients" },
        { label: "Radar příležitostí", path: "/crm/radar-prilezitosti" },
        { label: "Mapa klientů",     path: "/mapa-klientu" },
        { label: "Leady (Projekt 100)", path: "/crm/leads" },
      ] },
    { id: "obchody",      path: "/obchody",      label: "Obchody",     icon: Briefcase,
      children: [
        { label: "Obchodní nástěnka",  path: "/obchody" },
        { label: "Hypoteční pipeline", path: "/hypoteky" },
        { label: "Náborová pipeline",  path: "/nabor" },
        { label: "Náborový radar", path: "/nabor/cnb" },
        /* Jedna položka, ne dvě. „Zakázky (reality)" vedlo na druhý seznam
           téhož; od 9/2026 je přehled jen `/reality` (prodej i nákup) a karta
           je obchodní případ na `/obchody/:id`. Klíč `zakazkyReality` zůstává
           v NAV_KLIC a ve slovnících schválně — mazat překlad kvůli jedné
           přesunuté položce znamená rozbít pět jazyků naráz. */
        { label: "Realitní pipeline", path: "/reality" },
        /* Rezervační smlouva je VYPNUTÁ přepínačem (lib/funkce.js, Pan Čung
           2. 10. 2026). Položka se ptá až při renderu, takže zapnutí = jeden
           řádek v lib/funkce.js. Klíč `rezervacniSmlouva` v NAV_KLIC zůstává. */
        ...(zapnuto("rezervacniSmlouva")
          ? [{ label: "Rezervační smlouva", path: "/rezervacni-smlouva" }]
          : []),
      ] },
    { id: "diar",         path: "/diar",         label: "Diář",        icon: Calendar },
    { id: "posta",        path: "/posta",        label: "Pošta",       icon: Mail },
    ...(maTymUzivatel
      ? [{ id: "manazer", path: "/manazer", label: "Manažer", icon: UserCheck,
          children: [
            { label: "Přehled týmu",   path: "/manazer" },
            // IK konzultace existují na /crm/ik, ale nebyly nikde v hlavním menu —
            // manažer o nich nemohl vědět.
            { label: "IK konzultace",  path: "/crm/ik" },
            { label: "Moji poradci",   path: "/crm/team" },
            // Vyhlášení výsledků (žebříček → diplomy → prezentace na projektor).
            // Na Vyhlášení je i panel „Úkoly z porady" — odsud manažer zadá úkol
            // asistentovi/poradci přímo do JEHO diáře (components/manazer/ukoly).
            { label: "Vyhlášení výsledků", path: "/crm/vyhlaseni" },
            // Týmové akce (porady, teambuildingy) — bez položky v menu by se
            // manažer na obrazovku nedostal jinak než ručně zadanou adresou.
            { label: "Týmové akce",    path: "/crm/akce" },
            // Diář týmu — náhled do diáře poradce podle JEHO souhlasu.
            { label: "Diář týmu",      path: "/crm/diar-tymu" },
          ] }] : []),
    { id: "vzdelavani",   path: "/akademie/produkty", label: "Vzdělávání",  icon: GraduationCap,
      children: [
        /* Adaptace první (V7, 19. 10. 2026): dřív v menu chyběla a kdo vstoupil
           přes menu, nenašel ji. „Adaptace týmu“ jen tomu, kdo má tým. */
        { label: "7denní adaptace",                 path: "/akademie/adaptace" },
        { label: "Start nováčka (Cesta 90 dní)",    path: "/akademie/start" },
        { label: "Akademie — moduly & infografiky", path: "/akademie/produkty" },
        { label: "Metodika: Sestavení plánu",       path: "/akademie/metodika" },
        { label: "Zkouška & certifikát",            path: "/akademie/zkouska" },
        { label: "Skripty & námitky",               path: "/akademie/skripty" },
        { label: "Kde shánět klienty",              path: "/akademie/akvizice" },
        ...(maTymUzivatel ? [{ label: "Adaptace týmu", path: "/akademie/manazer" }] : []),
        /* Vzdělávací videa jsou VYPNUTÁ přepínačem (lib/funkce.js, Pan Čung
           2. 10. 2026) — videa se teprve natáčejí. Ptá se až při renderu, takže
           zapnutí = jeden řádek v lib/funkce.js. Klíč `videaKurzy` v NAV_KLIC
           i v SK_NAV zůstává. */
        ...(zapnuto("vzdelavaciVidea")
          ? [{ label: "Videa & kurzy",                path: "/vzdelavani" }]
          : []),
      ] },
    { id: "tools",        path: "/tools",        label: "Nástroje",    icon: Wrench,
      children: [
        { label: "Rozcestník nástrojů",     path: "/tools" },
        { label: "Finanční matematika",     path: "/fin-matematika" },
        { label: "Kalkulačky (20)",          path: "/calculators" },
        ...(zeme === "CZ" ? [{ label: "Penzijní kalkulačka", path: "/penze" }] : []),
        { label: "Životní pojištění",        path: "/zivotni-pojisteni" },
        { label: "Historická kalkulačka",    path: "/historicka-kalkulacka" },
        { label: "Investiční strategie",     path: "/investicni-strategie" },
        { label: "Srovnávač fondů",          path: "/srovnavac-fondu" },
        { label: "Nemovitost na pronájem",   path: "/nemovitost-pronajem" },
        { label: "Provizní kalkulačka",      path: "/provize" },
      ] },
  ];

  // Admin odkazy přesunuty do Nastavení (sekce Administrace) — sidebar zůstává čistý.
  const adminSection = [];

  const isActive = (path) => {
    if (path === "/klienti") return location.pathname.startsWith("/klienti");
    if (path === "/obchody") return location.pathname.startsWith("/obchody") || location.pathname.startsWith("/crm/hypoteky");
    if (path === "/crm") return (location.pathname === "/" || location.pathname.startsWith("/crm")) && !location.pathname.startsWith("/crm/leadgen");
    if (path === "/diar") return location.pathname.startsWith("/diar");
    if (path === "/posta") return location.pathname.startsWith("/posta");
    if (path === "/tools") return location.pathname.startsWith("/tools") || location.pathname.startsWith("/calculators");
    if (path === "/akademie/produkty") return location.pathname.startsWith("/akademie") || location.pathname === "/vzdelavani" || location.pathname.startsWith("/series");
    if (path === "/manazer") return location.pathname.startsWith("/manazer");
    return location.pathname === path;
  };

  const onSectionClick = (path) => {
    // When clicking a sidebar item, also close any open client folder overlay
    // so user returns to the section page.
    window.dispatchEvent(new CustomEvent("close-all-client-folders"));
    navigate(path);
    if (isMobile) setDrawerOpen(false);
  };

  return (
    <>
    {/* Backdrop — jen na mobilu, když je drawer otevřený. Klik zavře. */}
    {isMobile && drawerOpen && (
      <div
        className="fixed inset-0 z-[95]"
        style={{ background: "var(--scrim)", backdropFilter: "blur(2px)" }}
        onClick={() => setDrawerOpen(false)}
        data-testid="sidebar-backdrop"
        aria-hidden
      />
    )}
    <aside
      className="ink-board fixed top-0 left-0 bottom-0 flex flex-col"
      style={{
        // Mobil: pevná šířka draweru (rozbalený), off-canvas přes transform.
        // Desktop: šířku řídí JEN připnutí (žádné hover-rozbalování).
        width: isMobile ? W_EXPANDED : (expanded ? W_EXPANDED : W_COLLAPSED),
        zIndex: isMobile ? 96 : 90,
        // POZOR: na desktopu MUSÍ být transform "none". Jakýkoli transform (i translateX(0))
        // udělá z <aside> containing block pro position:fixed potomky → flyout submenu se pak
        // počítá vůči lište (200px, overflowX:hidden) a ořízne se. Mobil transform potřebuje (drawer).
        transform: isMobile ? (drawerOpen ? "translateX(0)" : "translateX(-100%)") : "none",
        background: "linear-gradient(180deg, var(--sidebar-2) 0%, var(--sidebar) 100%)",
        color: "var(--sidebar-ink)",
        boxShadow: isMobile
          ? (drawerOpen ? "4px 0 24px var(--sidebar-shadow-drawer)" : "none")
          : "2px 0 12px var(--sidebar-shadow)",
        transition: isMobile
          ? "transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)"
          : "width 0.22s cubic-bezier(0.32, 0.72, 0, 1)",
        overflowX: "hidden",
        borderRight: "1px solid var(--sidebar-line)",
        // Bezpečná zóna nahoře pro notch (iOS) při plné výšce draweru.
        paddingTop: isMobile ? "env(safe-area-inset-top)" : undefined,
      }}
      data-testid="left-sidebar"
      aria-hidden={isMobile && !drawerOpen ? true : undefined}
    >
      {/* Logo → nástěnka. Vráceno 17. 8. 2026 na přání majitele: „logo musí být vlevo,
          když na to kliknu, jdu na nástěnku přece." (7. 7. byl nápis odstraněný, ale
          tím zmizela i jediná cesta domů jedním klikem — fotka vedle vede do Nastavení.) */}
      <button
        onClick={() => onSectionClick("/crm")}
        className={`flex items-center ${expanded ? "px-3 gap-0" : "justify-center px-0"} pt-3 pb-2 flex-shrink-0 w-full`}
        title={t("lista.naNastenku", "Na nástěnku")}
        data-testid="sidebar-logo"
      >
        {/* ZNAČKA — #7FA9E8 / #F47920 zůstávají natvrdo ve VŠECH motivech.
            Logo se nepřebarvuje podle vzhledu CRM, stejně jako na vizitce či v tisku. */}
        {expanded ? (
          <span className="flex items-end leading-none" style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-0.02em" }}>
            <span style={{ color: "#7FA9E8" }}>be</span>
            <span style={{ color: "#F47920" }}>plan</span>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" className="mb-0.5 ml-0.5">
              <path d="M7 17L17 7M17 7H9M17 7V15" stroke="#F47920" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ) : (
          <span className="flex items-end leading-none" style={{ fontSize: 17, fontWeight: 800 }}>
            <span style={{ color: "#7FA9E8" }}>b</span>
            <span style={{ color: "#F47920" }}>p</span>
          </span>
        )}
      </button>

      {/* Hlavička: fotka poradce + Hledat (à la Raynet) */}
      <div
        className={`flex ${expanded ? "items-center px-3" : "flex-col items-center gap-2 px-0"} pb-3 flex-shrink-0`}
        style={{ borderBottom: "1px solid var(--sidebar-line)" }}
      >
        <button
          onClick={() => onSectionClick("/nastaveni")}
          className="flex-shrink-0 rounded-full overflow-hidden"
          style={{ width: 32, height: 32, boxShadow: "0 2px 6px rgba(0,0,0,0.25)" }}
          title={t("lista.profilPoradce", "Profil poradce (fotku nastavíš v Nastavení)")}
          data-testid="sidebar-avatar"
        >
          {advisorPhoto ? (
            <img src={advisorPhoto} alt="" className="w-full h-full object-cover" />
          ) : (
            <span
              className="w-full h-full flex items-center justify-center font-bold text-[12px]"
              style={{ background: "var(--sidebar-cta)", color: "var(--sidebar-cta-ink)" }}
            >
              {advisorInitials}
            </span>
          )}
        </button>
        {expanded ? (
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("open-global-search"))}
            className="ml-2.5 flex-1 min-w-0 flex items-center gap-2 rounded-lg px-2.5 py-2 text-[12px]"
            style={{ background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.1)" }}
            data-testid="sidebar-search"
            title={t("lista.hledatVCeleAplikaci", "Hledat v celé aplikaci")}
          >
            <Search size={13} className="flex-shrink-0" />
            <span className="flex-1 text-left truncate">{navT("Hledat", t)}…</span>
            <kbd className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.75)" }}>{kbdHint}</kbd>
          </button>
        ) : (
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("open-global-search"))}
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.85)" }}
            data-testid="sidebar-search"
            title={`${t("lista.hledat", "Hledat")} (${kbdHint})`}
          >
            <Search size={14} />
          </button>
        )}
        {/* Zavřít drawer — jen mobil */}
        {isMobile && (
          <button
            onClick={() => setDrawerOpen(false)}
            className="ml-2 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ color: "var(--sidebar-dim)", background: "rgba(255,255,255,0.06)" }}
            data-testid="sidebar-drawer-close"
            aria-label={t("lista.zavritMenu", "Zavřít menu")}
            title={t("lista.zavritMenu", "Zavřít menu")}
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Sections */}
      <nav className="flex-1 overflow-y-auto py-2">
        {sections.map((s) => (
          <SidebarItem key={s.id} item={s} active={isActive(s.path)} expanded={expanded} isMobile={isMobile} onClick={onSectionClick} />
        ))}

        {adminSection.length > 0 && (
          <>
            <div className="mt-3 mb-1 px-4 text-[9px] uppercase" style={{ color: "var(--sidebar-muted)", letterSpacing: "0.2em" }}>
              {expanded ? "Admin" : "·"}
            </div>
            {adminSection.map((s) => (
              <SidebarItem key={s.id} item={s} active={isActive(s.path)} expanded={expanded} isMobile={isMobile} onClick={onSectionClick} />
            ))}
          </>
        )}
      </nav>

      {/* Nový záznam — statické primární CTA (jako „Nový záznam" v Raynetu), menu typů */}
      <button
        ref={newBtnRef}
        onClick={openNewMenu}
        className="mx-2 mb-1.5 flex items-center px-3 py-2.5 rounded-full transition-all hover:translate-y-[-1px]"
        /* --sidebar-cta: v Lime neon s černým písmem (černý akcent by na černé liště zmizel, 1,20 : 1). */
        style={{ background: "var(--sidebar-cta)", color: "var(--sidebar-cta-ink)", boxShadow: "0 4px 12px var(--accent-glow)" }}
        data-testid="sidebar-new-record"
        title={t("lista.novyZaznam", "Nový záznam — klient, lead, obchod, úkol…")}
      >
        <Plus size={16} className="flex-shrink-0" style={{ transform: newMenuOpen ? "rotate(45deg)" : "none", transition: "transform .2s" }} />
        {expanded && <span className="ml-2.5 text-[12px] font-bold tracking-tight truncate">{t("lista.novyZaznamTlacitko", "Nový záznam")}</span>}
      </button>
      {newMenuOpen && menuPos && createPortal(
        <>
          <div className="fixed inset-0 z-[150]" onClick={() => setNewMenuOpen(false)} data-testid="sidebar-new-overlay" />
          <div className="ink-board fixed z-[151] py-1.5 rounded-xl"
            style={{ left: menuPos.left, bottom: menuPos.bottom, minWidth: 240, background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-float)", color: "var(--ink)" }}
            data-testid="sidebar-new-menu">
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>{t("lista.novyZaznamTlacitko", "Nový záznam")}</div>
            {newActions.map((a) => (
              <button key={a.id} onClick={a.onClick} data-testid={`sidebar-new-${a.id}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors"
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-soft)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                {/* Dřív `${a.accent}1F` / `55` — lepení alfy na hex nefunguje, když
                    je barva token (var(--accent)). color-mix umí obojí; procenta
                    jsou přesně 0x1F/255 a 0x55/255, takže se odstín nehnul. */}
                <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `color-mix(in srgb, ${a.accent} 12.157%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${a.accent} 33.333%, transparent)`,
                    color: a.accent,
                  }}>
                  <a.icon size={15} />
                </span>
                <span className="min-w-0">
                  <span className="block text-[12.5px] font-semibold leading-tight" style={{ color: "var(--ink)" }}>{a.label}</span>
                  <span className="block text-[9.5px] uppercase tracking-wider leading-tight" style={{ color: "var(--muted)" }}>{a.sub}</span>
                </span>
              </button>
            ))}
          </div>
        </>, document.body)}

      {/* Co je nového — tečka, dokud poradce neviděl nejnovější vydání (SK partner 23. 10. 2026). */}
      <OdkazNovinky expanded={expanded} active={isActive("/novinky")} onClick={onSectionClick} />

      {/* Voice Field Mode — dočasně SKRYTO na přání (pan Čung: teď nepotřebuji). Route /voice-field zůstává funkční. */}
      {false && (
      <button
        onClick={() => navigate("/voice-field")}
        className="mx-2 mb-2 mt-1 flex items-center px-3 py-2 rounded-full transition-all hover:translate-y-[-1px]"
        style={{
          // Blok se nerenderuje (`false &&`), ale barvy držíme na tokenech —
          // až se vrátí, nesmí přijít v Indigu inkoustový.
          background: "color-mix(in srgb, var(--accent) 18%, transparent)",
          color: "var(--accent-pale)",
          border: "1px solid color-mix(in srgb, var(--accent-lift) 55%, transparent)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.10) inset",
        }}
        data-testid="sidebar-voice-field"
        title="Voice Field Mode — hands-free režim za volantem"
      >
        <Mic size={15} className="flex-shrink-0" />
        {expanded && <span className="ml-2.5 text-[11.5px] font-bold tracking-tight truncate">Voice Field</span>}
      </button>
      )}

      {/* Footer — jen sbalit/rozbalit; na mobilu (drawer) nedává smysl, skryjeme. */}
      {!isMobile && (
        <div
          className="flex-shrink-0"
          style={{ borderTop: "1px solid var(--sidebar-line)" }}
        >
          <button
            onClick={() => setLocked((v) => !v)}
            className="w-full flex items-center px-4 py-2 text-[11px] transition-colors"
            style={{ color: "var(--sidebar-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.06)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            data-testid="sidebar-toggle-lock"
            title={locked ? t("lista.sbalitListu", "Sbalit lištu") : t("lista.pripnoutRozbalene", "Připnout rozbalené")}
          >
            {locked ? <ChevronsLeft size={13} /> : <ChevronsRight size={13} />}
            {expanded && <span className="ml-3">{navT(locked ? "Sbalit" : "Připnout", t)}</span>}
          </button>
        </div>
      )}
      {/* Mobil: spodní bezpečná zóna (home indicator), ať poslední CTA není oříznuté. */}
      {isMobile && <div className="flex-shrink-0" style={{ height: "env(safe-area-inset-bottom)" }} />}
    </aside>
    </>
  );
}

function SidebarItem({ item, active, expanded, isMobile, onClick }) {
  const { t } = useLanguage();
  const Icon = item.icon;
  const [hovered, setHovered] = useState(false);
  const [flyPos, setFlyPos] = useState(null);
  const wrapRef = useRef(null);
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  // Desktop: podpoložky VŽDY jako flyout DOPRAVA na hover (v sbalené i připnuté liště) —
  // feedback 7. 7.: „mělo by vyjet doprava, ne dolů". Šířka lišty je statická,
  // flyout navazuje na její hranu bez mezery a je potomkem wrapperu → nebliká.
  // Mobil (drawer, hover neexistuje): inline rozbalení na klik.
  const [open, setOpen] = useState(isMobile && active && hasChildren);
  useEffect(() => { if (isMobile && active && hasChildren) setOpen(true); }, [isMobile, active, hasChildren]);

  const handleEnter = () => {
    setHovered(true);
    if (!isMobile && hasChildren) {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setFlyPos({ top: Math.min(r.top, window.innerHeight - 220), left: expanded ? W_EXPANDED : W_COLLAPSED });
    }
  };

  const handleMainClick = () => {
    // Mobil s podpoložkami: klik NEJEN rozbalí, ale i naviguje na hlavní stránku
    // sekce. Dřív jen toggloval submenu — majitel na telefonu klikl na „Manažer",
    // nic se neotevřelo a nahlásil „není tlačítko, nic nefunguje" (17. 9. 2026).
    if (isMobile && hasChildren) setOpen(true);
    onClick(item.path);
  };

  const showFlyout = !isMobile && hasChildren && hovered && flyPos;

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        onClick={handleMainClick}
        className="w-full flex items-center px-4 py-2 transition-colors group relative"
        style={{
          background: active || hovered ? "var(--sidebar-active-bg)" : "transparent",
          // Světlejší text (feedback: „ať je to přehledný, ať vidí") — skoro bílá i v klidu.
          // Bílá s alfou je v pořádku: lišta je tmavá v obou motivech (viz hlavička).
          color: active ? "var(--sidebar-active-ink)" : "rgba(255,255,255,0.96)",
        }}
        data-testid={`sidebar-${item.id}`}
        title={navT(item.label, t)}
        aria-label={navT(item.label, t)}
        aria-current={active ? "page" : undefined}
        aria-expanded={hasChildren && isMobile ? open : undefined}
      >
        {active && (
          <span
            aria-hidden
            className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r"
            style={{ width: 3, height: 18, background: "var(--sidebar-marker)", boxShadow: "0 0 8px var(--sidebar-marker-glow)" }}
          />
        )}
        <Icon size={15} className="flex-shrink-0" />
        {expanded && <span className="ml-3 text-[12px] font-medium truncate flex-1 text-left">{navT(item.label, t)}</span>}
        {/* Šipka doprava = podmenu vyjede flyoutem; na mobilu se otočí dolů (inline). */}
        {expanded && hasChildren && (
          <ChevronRight size={11} style={{ opacity: 0.7, transform: isMobile && open ? "rotate(90deg)" : "none", transition: "transform .18s ease" }} />
        )}
        {/* Sbalený režim: malá šipka jako náznak, že položka má podmenu (flyout na hover). */}
        {!expanded && hasChildren && (
          <ChevronRight
            size={9}
            aria-hidden
            className="absolute right-1 top-1/2 -translate-y-1/2"
            style={{ opacity: hovered ? 0.95 : 0.5 }}
          />
        )}
      </button>

      {/* Inline podpoložky — JEN mobil (drawer, hover neexistuje). Na desktopu flyout níže. */}
      {hasChildren && isMobile && open && (
        <div className="pb-1" data-testid={`sidebar-submenu-${item.id}`}>
          {item.children.map((c) => (
            <button
              key={c.path}
              onClick={() => onClick(c.path)}
              className="w-full text-left pl-11 pr-3 py-1.5 text-[11.5px] transition-colors"
              style={{ color: "rgba(255,255,255,0.92)", background: "transparent" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--sidebar-hover-bg)"; e.currentTarget.style.color = "var(--sidebar-active-ink)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.92)"; }}
              data-testid={`sidebar-sub-${c.path.replace(/[^a-z0-9]/gi, '-')}`}
            >
              {navT(c.label, t)}
            </button>
          ))}
        </div>
      )}

      {/* Flyout — desktop, OBA režimy lišty (hover). position:fixed (nav má overflow),
          left = šířka lišty (statická → navazuje na tlačítko bez mezery).
          Pevná šířka 224 (fixed prvek jinak dostane shrink-to-fit ~386px). */}
      {showFlyout && (
        <div
          className="z-[200] py-1 rounded-lg"
          style={{
            position: "fixed",
            top: flyPos.top,
            left: flyPos.left,
            width: 224,
            // Flyout je potomek .ink-board lišty → tokeny motivu tu platí.
            // Hexové fallbacky byly zbytečné a hlavně zastaralé (sage barvy).
            background: "var(--surface)",
            border: "1px solid var(--line)",
            boxShadow: "var(--shadow-float)",
            color: "var(--ink)",
          }}
          data-testid={`sidebar-flyout-${item.id}`}
        >
          <div className="px-3 pt-1 pb-1.5 text-[9px] uppercase tracking-[0.16em] font-bold" style={{ color: "var(--muted)" }}>
            {navT(item.label, t)}
          </div>
          {item.children.map((c) => (
            <button
              key={c.path}
              onClick={() => { setHovered(false); onClick(c.path); }}
              className="w-full text-left px-3 py-1.5 text-[12px] rounded-md"
              style={{ color: "var(--ink)", background: "transparent" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--accent-soft)"; e.currentTarget.style.color = "var(--accent)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--ink)"; }}
              data-testid={`sidebar-flyout-link-${c.path.replace(/[^a-z0-9]/gi, '-')}`}
            >
              {navT(c.label, t)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Export width constants for layout offset
export { W_COLLAPSED, W_EXPANDED };
